import React, { useMemo, useCallback, useState } from "react";
import { getTranslation, getLocalizedValue } from "../../utils/translate.js";
import { DISH_IMAGE_PLACEHOLDER_64 } from "../../utils/dishImagePlaceholder.js";
import { useLanguage } from "../../utils/languageContext.js";

const rowHighlightKey = (companyKey, variantKey) => `${String(companyKey)}::${String(variantKey)}`;

const additionId = (item) => item?.dishAdditionDto?.id ?? item?.id ?? null;

/** Stable key so the same dish with different additions stays on separate rows. */
const dishVariantKey = (line) => {
    const dishId = line?.dishDto?.id;
    if (!dishId) return null;
    const list = Array.isArray(line?.orderDishAdditionDtoList) ? line.orderDishAdditionDtoList : [];
    const ids = list
        .map(additionId)
        .filter((id) => id != null)
        .map(String)
        .sort();
    return `${dishId}::${ids.join(",")}`;
};

/** Aggregates dish quantities from the same ongoing orders payload as the list tab, grouped by company. */
const KitchenSummary = ({ orders }) => {
    const { language } = useLanguage();

    /** Ephemeral: parent remounts this component when ongoing list UI context changes. */
    const [rowHighlights, setRowHighlights] = useState({});

    const labelCompany = useCallback((name) => {
        const trimmed = name?.trim();
        if (trimmed) return trimmed;
        return getTranslation("orderPage.noCompany", language) || "—";
    }, [language]);

    const byCompany = useMemo(() => {
        const map = new Map();

        if (orders && orders.length > 0) {
            orders.forEach((order) => {
                if (!order) return;
                const cKey = order.companyName?.trim() || "";
                if (!map.has(cKey)) {
                    map.set(cKey, {
                        companyKey: cKey,
                        companyDisplay: labelCompany(order.companyName),
                        totalPieces: 0,
                        dishMap: new Map()
                    });
                }
                const bucket = map.get(cKey);
                if (order.orderDishList && order.orderDishList.length > 0) {
                    order.orderDishList.forEach((line) => {
                        const variantKey = dishVariantKey(line);
                        const qty = line?.quantity || 1;
                        if (!variantKey) return;
                        bucket.totalPieces += qty;
                        if (!bucket.dishMap.has(variantKey)) {
                            bucket.dishMap.set(variantKey, {
                                variantKey,
                                dishDto: line.dishDto,
                                additions: Array.isArray(line.orderDishAdditionDtoList)
                                    ? line.orderDishAdditionDtoList
                                    : [],
                                totalQuantity: 0
                            });
                        }
                        bucket.dishMap.get(variantKey).totalQuantity += qty;
                    });
                }
            });
        }

        return Array.from(map.values())
            .map((b) => ({
                ...b,
                dishes: Array.from(b.dishMap.values()).sort((a, c) => c.totalQuantity - a.totalQuantity)
            }))
            .sort((a, b) => b.totalPieces - a.totalPieces);
    }, [orders, labelCompany]);

    const cycleRowHighlight = useCallback((companyKey, variantKey) => {
        const key = rowHighlightKey(companyKey, variantKey);
        setRowHighlights((prev) => {
            const cur = prev[key] || 0;
            const next = cur === 0 ? 1 : cur === 1 ? 2 : 0;
            if (next === 0) {
                const { [key]: _removed, ...rest } = prev;
                return rest;
            }
            return { ...prev, [key]: next };
        });
    }, []);

    if (!orders || orders.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center p-8 bg-white dark:bg-[#161b22] rounded-xl border border-gray-100 dark:border-[#30363d]">
                <p className="text-gray-500 dark:text-[#8b949e] font-medium">
                    {getTranslation("orderPage.noDataFound", language) || "No data found"}
                </p>
            </div>
        );
    }

    if (byCompany.length === 0 || byCompany.every((b) => b.totalPieces === 0)) {
        return (
            <div className="flex flex-col items-center justify-center p-8 bg-white dark:bg-[#161b22] rounded-xl border border-gray-100 dark:border-[#30363d]">
                <p className="text-gray-500 dark:text-[#8b949e] font-medium">
                    {getTranslation("orderPage.noDataFound", language) || "No dishes to prepare"}
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {byCompany.map((block) => (
                <div
                    key={block.companyKey || "__empty__"}
                    className="rounded-xl border border-gray-200 dark:border-[#30363d] bg-white dark:bg-[#161b22] shadow-sm overflow-hidden"
                >
                    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-gray-50/90 dark:bg-[#21262d]/60 border-b border-gray-200 dark:border-[#30363d]">
                        <h3 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-[#c9d1d9]">
                            {block.companyDisplay}
                        </h3>
                        <span className="text-xs sm:text-sm font-bold tabular-nums px-2.5 py-1 rounded-lg bg-gray-200/80 dark:bg-[#30363d] text-gray-900 dark:text-[#c9d1d9]">
                            {getTranslation("orderPage.dishPiecesTotal", language) || "Pieces"}:{" "}
                            {block.totalPieces}
                        </span>
                    </div>
                    <ul className="divide-y divide-gray-100 dark:divide-[#30363d]">
                        {block.dishes.map((item) => {
                            const rk = rowHighlightKey(block.companyKey, item.variantKey);
                            const hi = rowHighlights[rk];
                            const rowBg =
                                hi === 1
                                    ? "bg-yellow-100/95 dark:bg-yellow-950/30 hover:bg-yellow-100 dark:hover:bg-yellow-950/40"
                                    : hi === 2
                                      ? "bg-green-100/95 dark:bg-green-950/35 hover:bg-green-100 dark:hover:bg-green-950/45"
                                      : "hover:bg-gray-50/80 dark:hover:bg-[#0d1117]/40";
                            return (
                                <li
                                    key={rk}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => cycleRowHighlight(block.companyKey, item.variantKey)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            cycleRowHighlight(block.companyKey, item.variantKey);
                                        }
                                    }}
                                    className={`flex items-center justify-between gap-3 px-4 py-3 cursor-pointer select-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gray-400 dark:focus-visible:ring-[#58a6ff]/50 ${rowBg}`}
                                >
                                    <div className="flex items-center gap-3 min-w-0 flex-1">
                                        <img
                                            src={item.dishDto.url || DISH_IMAGE_PLACEHOLDER_64}
                                            alt={getLocalizedValue(item.dishDto, "name", language)}
                                            className="w-12 h-12 sm:w-14 sm:h-14 object-cover rounded-lg shrink-0"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = DISH_IMAGE_PLACEHOLDER_64;
                                            }}
                                        />
                                        <div className="min-w-0 flex-1">
                                            <span className="text-sm font-medium text-gray-900 dark:text-[#c9d1d9] line-clamp-2">
                                                {getLocalizedValue(item.dishDto, "name", language) || "—"}
                                            </span>
                                            {item.additions.length > 0 && (
                                                <ul className="mt-1 space-y-0.5">
                                                    {item.additions.map((addition, idx) => {
                                                        const groupName = getLocalizedValue(
                                                            addition?.dishAdditionDto?.group,
                                                            "name",
                                                            language
                                                        );
                                                        const additionName = getLocalizedValue(
                                                            addition?.dishAdditionDto,
                                                            "name",
                                                            language
                                                        );
                                                        return (
                                                            <li
                                                                key={additionId(addition) ?? idx}
                                                                className="text-xs text-gray-600 dark:text-[#8b949e] line-clamp-1"
                                                            >
                                                                {groupName ? `${groupName}: ` : ""}
                                                                {additionName || "—"}
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            )}
                                        </div>
                                    </div>
                                    <span className="text-sm font-bold tabular-nums text-gray-800 dark:text-[#e6edf3] shrink-0 w-10 text-right">
                                        {item.totalQuantity}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            ))}
        </div>
    );
};

export default KitchenSummary;
